import { getGlobalHeadersAsObject } from "../global-headers";
import type { DRFListResponseWrapper } from "../types";

type PersonAttachmentQueryParams = {
	person?: number;
	deleted?: boolean;
};

export type BlobReplicaDebug = {
	location: string;
	provider: string;
	state: string;
	key: string;
};

export type BlobDebug = {
	blob_id: number | null;
	sha256: string | null;
	size: number | null;
	content_type: string | null;
	attachment_url: string;
	replicas: BlobReplicaDebug[];
};

export type PersonAttachment = {
	id?: number;
	attachment_url?: string;
	created?: string;
	updated?: string;
	deleted?: boolean;
	person: number;
	file?: string;
	name: string;
	type: string;
	/** StoredBlob pk backing this CV (null for not-yet-onboarded legacy rows). */
	blob?: number | null;
	/** Admin-only storage diagnostics; null for non-superusers. */
	blob_debug?: BlobDebug | null;
};

export const getAttachments = (
	params?: PersonAttachmentQueryParams
): Promise<DRFListResponseWrapper<PersonAttachment[]>> => {
	let url = "/api/v2/person_attachment/";
	if (params !== undefined) {
		const queryParams = Object.keys(params).map((key) => {
			return [key, params[key]];
		});
		const queryParamsString = new URLSearchParams(queryParams).toString();
		url += "?" + queryParamsString;
	}

	return fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Fetch a CV's bytes from our authenticated API (bucket is private) and return a
 * blob: object URL for display. A plain <a>/<iframe> can't carry the JWT, so the
 * client must fetch with auth headers rather than link straight to storage.
 * Callers must URL.revokeObjectURL(url) when done to avoid leaking memory.
 */
export const fetchAttachmentObjectUrl = async (id: number): Promise<string> => {
	const { blob } = await fetchAttachmentBlob(id);
	return URL.createObjectURL(blob);
};

/**
 * Fetch a CV's bytes + server Content-Type for inline rendering (PDF iframe,
 * DOCX client-side render). Same auth/private-bucket constraints as
 * fetchAttachmentObjectUrl — the caller owns the returned blob.
 */
export const fetchAttachmentBlob = (
	id: number
): Promise<{ blob: Blob; contentType: string }> =>
	fetchBlobFromUrl(`/api/v2/person_attachment/${id}/download/`);

/**
 * Fetch any authenticated download endpoint's bytes + Content-Type. Used by the
 * attachment viewer so it can render from any source (person attachments, the
 * admin CV browser, …) rather than being hard-wired to one URL.
 */
export const fetchBlobFromUrl = async (
	url: string
): Promise<{ blob: Blob; contentType: string }> => {
	const res = await fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		throw new Error(`HTTP status code: ${res.status}`);
	}
	const blob = await res.blob();
	return { blob, contentType: res.headers.get("Content-Type") ?? "" };
};

export const uploadAttachment = (
	file: File,
	personId: number
): Promise<PersonAttachment> => {
	const data = new FormData();
	data.append("file", file);
	data.append("name", file.name);
	data.append("type", file.type);
	data.append("person", personId.toString());

	return fetch("/api/v2/person_attachment/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
		},
		body: data,
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP status code: ${res.status}`);
		}
		return res.json();
	});
};

/**
 * Does not actually delete the assignment, instead it just marks the record as deleted...
 */
export const deleteAttachment = (
	personAttachment: PersonAttachment
): Promise<PersonAttachment> => {
	const deletedAttachment = { ...personAttachment, deleted: true };
	return fetch(`/api/v2/person_attachment/${personAttachment.id}/`, {
		method: "PUT",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(deletedAttachment),
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP status code: ${res.status}`);
		}
		return res.json();
	});
};
